import { isBrowser } from '../window/detection';
import { OPERATING_SYSTEM } from './constants';

/**
 * Determines the operating system of the current environment.
 *
 * Detection strategy (in order):
 * 1. Use User-Agent Client Hints (`navigator.userAgentData`) when available.
 *    - Modern browsers expose structured, privacy-aware client hints via
 *      `navigator.userAgentData`. We prefer this because it provides
 *      normalized, high-level properties (like `platform`) without parsing
 *      opaque user-agent strings. It reduces parsing errors and is less
 *      susceptible to variation and spoofing than `navigator.userAgent`.
 *    - Note: `userAgentData` originates from the User-Agent Client Hints
 *      specification and is the future-facing way to access UA information.
 *
 * 2. Fallback to the legacy `navigator.userAgent` / `navigator.vendor` string.
 *    - Older browsers (or some WebViews) may not support `userAgentData`,
 *      so we still parse the user-agent string as a reliable fallback.
 *
 * 3. When not running in a browser, use `process.platform` (Node.js).
 *
 * Notes on `navigator.platform` and deprecation:
 * - Historically code used `navigator.platform` to detect OS. That value is
 *   inconsistent across browsers and can be a fingerprinting surface. Many
 *   browsers have reduced its fidelity or recommend using Client Hints instead.
 * - Because of those privacy and consistency issues we avoid relying on
 *   `navigator.platform` as the primary source; it is only used indirectly
 *   or as a secondary indicator when available.
 *
 * iOS special cases:
 * - iOS UA strings and platform values are historically messy (iPhone/iPad
 *   vs. MacIntel for some iPadOS versions). We also check `maxTouchPoints`
 *   as a pragmatic signal for touch-capable Apple devices.
 *
 * Reliability note:
 * - All string matching uses word-bounded regexes (e.g. `\bwindows\b`) rather than loose
 *   substring checks (`includes('win')`), so an unrelated token elsewhere in the UA string
 *   can't be misread as a different OS.
 * - This detection can still be fooled by browser extensions or privacy tools that spoof
 *   `navigator.userAgentData` / `navigator.userAgent` (a known anti-fingerprinting technique).
 *   There is no fully spoof-proof client-side signal for this.
 *
 * @returns {string} The name of the operating system, or OPERATING_SYSTEM.UNKNOWN if it cannot be determined.
 */
// Word-bounded patterns avoid false positives from loose substring checks (e.g. a stray
// "win" or "mac" appearing inside an unrelated token of a WebView/in-app-browser UA string).
const WINDOWS_PATTERN = /\bwindows\b|\bwin32\b|\bwin64\b|\bwow64\b/i;
const MACOS_PATTERN = /\bmac ?os x\b|\bmacintosh\b/i;
const LINUX_PATTERN = /\blinux\b/i;
const IOS_DEVICE_PATTERN = /\bipad\b|\biphone\b|\bipod\b/i;
const ANDROID_PATTERN = /\bandroid\b/i;

/**
 * Detects the OS from User-Agent Client Hints (`navigator.userAgentData.platform`).
 * Only Chromium-based browsers expose this; others return `null` so callers fall back
 * to UA string parsing.
 */
function detectOperatingSystemFromClientHints(): string | null {
  if (!('userAgentData' in navigator)) {
    return null;
  }

  const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
  const platform = uaData && typeof uaData.platform === 'string' ? uaData.platform.toLowerCase() : '';

  if (!platform) {
    return null;
  }

  if (platform.includes('android')) {
    return OPERATING_SYSTEM.ANDROID;
  }

  if (platform.includes('ios')) {
    return OPERATING_SYSTEM.IOS;
  }

  if (platform.includes('mac')) {
    return OPERATING_SYSTEM.MACOS;
  }

  if (platform.includes('win')) {
    return OPERATING_SYSTEM.WINDOWS;
  }

  if (platform.includes('linux')) {
    return OPERATING_SYSTEM.LINUX;
  }

  return null;
}

/**
 * Detects the OS by parsing `navigator.userAgent` (and `navigator.platform` as a last resort).
 * Android is checked before Linux because Android UA strings also contain "Linux".
 */
function detectOperatingSystemFromUserAgent(): string | null {
  const userAgent = navigator.userAgent || '';
  const maxTouchPoints = (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints ?? 0;

  if (ANDROID_PATTERN.test(userAgent)) {
    return OPERATING_SYSTEM.ANDROID;
  }

  if (IOS_DEVICE_PATTERN.test(userAgent) || (maxTouchPoints > 1 && MACOS_PATTERN.test(userAgent))) {
    return OPERATING_SYSTEM.IOS;
  }

  if (WINDOWS_PATTERN.test(userAgent)) {
    return OPERATING_SYSTEM.WINDOWS;
  }

  if (MACOS_PATTERN.test(userAgent)) {
    return OPERATING_SYSTEM.MACOS;
  }

  if (LINUX_PATTERN.test(userAgent)) {
    return OPERATING_SYSTEM.LINUX;
  }

  // Deprecated but kept as a last resort for old/unusual UA strings that omit OS info.
  const platform = navigator.platform || '';

  if (MACOS_PATTERN.test(platform) || /^mac/i.test(platform)) {
    return OPERATING_SYSTEM.MACOS;
  }

  if (WINDOWS_PATTERN.test(platform) || /^win/i.test(platform)) {
    return OPERATING_SYSTEM.WINDOWS;
  }

  if (LINUX_PATTERN.test(platform)) {
    return OPERATING_SYSTEM.LINUX;
  }

  return null;
}

export function getOperatingSystem(): string {
  if (isBrowser()) {
    const operatingSystem = detectOperatingSystemFromClientHints() ?? detectOperatingSystemFromUserAgent();

    if (operatingSystem) {
      return operatingSystem;
    }
  } else if (typeof process !== 'undefined') {
    return process.platform;
  }

  return OPERATING_SYSTEM.UNKNOWN;
}
